import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ProductGrid from "@/components/catalog/ProductGrid";
import ProductGridSkeleton from "@/components/catalog/ProductGridSkeleton";
import ProductFilters from "@/components/catalog/ProductFilters";
import DiscountPopup from "@/components/catalog/DiscountPopup";
import PageSizeSelector from "@/components/catalog/PageSizeSelector";
import CatalogPagination from "@/components/catalog/CatalogPagination";
import SEOHead from "@/components/SEOHead";
import { FloatingWhatsApp } from "@/components/ui/floating-whatsapp";
import { Skeleton } from "@/components/ui/skeleton";
import { useProducts } from "@/hooks/useProducts";
import { cn } from "@/lib/utils";

const Catalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get("category") || "all"
  );
  const [selectedBrand, setSelectedBrand] = useState(
    searchParams.get("brand") || ""
  );
  const [showDiscountPopup, setShowDiscountPopup] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);

  // Get search from URL params (from header search)
  const searchQuery = searchParams.get("search") || "";

  useEffect(() => {
    const alreadyClaimed = localStorage.getItem("tekton_discount_claimed") === "true";
    if (alreadyClaimed) return;

    const dismissedAt = localStorage.getItem("tekton_discount_dismissed_at");
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      const twentyFourHours = 24 * 60 * 60 * 1000;
      const now = Date.now();
      
      if (now - dismissedTime < twentyFourHours) {
        return;
      }
      localStorage.removeItem("tekton_discount_dismissed_at");
    }

    const timer = setTimeout(() => {
      setShowDiscountPopup(true);
    }, 10000);
    
    return () => clearTimeout(timer);
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedBrand, searchQuery]);

  const { data, isLoading, error } = useProducts({
    category: selectedCategory,
    brand: selectedBrand || undefined,
    search: searchQuery,
    page: currentPage,
    pageSize: pageSize,
  });

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    updateSearchParams(selectedBrand, category);
  };

  const handleBrandChange = (brand: string) => {
    setSelectedBrand(brand);
    updateSearchParams(brand, selectedCategory);
  };

  const updateSearchParams = (brand: string, category: string) => {
    const params: Record<string, string> = {};
    if (brand) params.brand = brand;
    if (category !== "all") params.category = category;
    if (searchQuery) params.search = searchQuery;
    setSearchParams(params);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const getPageTitle = () => {
    if (searchQuery) return `Resultados para "${searchQuery}"`;
    return "Catálogo de Productos";
  };

  const getPageDescription = () => {
    return "Explorá nuestra amplia variedad de productos tecnológicos: cámaras de seguridad, equipos de cómputo y accesorios";
  };

  const getSeoKeywords = () => {
    return "tecnología, cámaras de seguridad, computadoras, accesorios tecnológicos, Costa Rica, Tekton";
  };

  const products = data?.products || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = data?.totalPages || 1;

  // Calculate display range
  const fromItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const toItem = Math.min(currentPage * pageSize, totalCount);

  // Build breadcrumbs for SEO
  const breadcrumbs = [
    { name: "Inicio", url: "/" },
    { name: "Catálogo", url: "/catalogo" },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title={getPageTitle()}
        description={getPageDescription()}
        keywords={getSeoKeywords()}
        breadcrumbs={breadcrumbs}
      />
      <Header />

      <main className="flex-1">
        {/* Page header */}
        <div className="bg-primary text-white py-8">
          <div className="container mx-auto px-4">
            <h1 className="text-2xl md:text-3xl font-bold">{getPageTitle()}</h1>
            <p className="text-white/80 mt-1">
              {getPageDescription()}
            </p>
          </div>
        </div>

        {/* Main content */}
        <div className="container mx-auto px-4 py-6">
          {/* Filters */}
          <ProductFilters
            selectedCategory={selectedCategory}
            selectedBrand={selectedBrand}
            onCategoryChange={handleCategoryChange}
            onBrandChange={handleBrandChange}
          />

          {/* Results count and page size selector */}
          <div className={cn(
            "flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6",
            "p-4 rounded-lg bg-muted border border-border"
          )}>
            {isLoading ? (
              <Skeleton className="h-5 w-48" />
            ) : error ? (
              <p className="text-destructive">Error al cargar productos</p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Mostrando <span className="font-semibold text-foreground">{fromItem}-{toItem}</span> de{" "}
                <span className="font-semibold text-foreground">{totalCount}</span> productos
              </p>
            )}
            <PageSizeSelector value={pageSize} onChange={handlePageSizeChange} />
          </div>

          {/* Products Grid */}
          {isLoading ? (
            <ProductGridSkeleton count={pageSize} />
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-destructive">Error al cargar los productos. Por favor intenta de nuevo.</p>
            </div>
          ) : (
            <>
              <ProductGrid products={products} />
              
              {/* Pagination */}
              {totalPages > 1 && (
                <CatalogPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
      <FloatingWhatsApp />

      {showDiscountPopup && (
        <DiscountPopup onClose={() => setShowDiscountPopup(false)} />
      )}
    </div>
  );
};

export default Catalog;
